import { useEffect, useState } from 'react';
import { fetchMeta, type Meta } from './api';
import { AdminApp } from './admin/app';
import { BrandPage } from './pages/brand';
import { ManagePage } from './pages/manage';
import { LandingPage, LegalPage } from './pages/site';

/**
 * Drei Welten, ein Aufbau:
 *   /, /impressum, /datenschutz   das Schaufenster evercal.de (vorgerendert, pages/site.tsx)
 *   /admin/…            Verwaltung (Anmeldung, Auftritte, Terminarten)
 *   /<auftritt>         Buchungsseite eines Auftritts
 *   /<auftritt>/<art>   Terminwahl
 *   /absage/<link>, /umbuchen/<link>   persönliche Links aus der Bestätigungsmail
 *   /passwort-neu/<link>               „Passwort vergessen"
 */
type Route =
  | { page: 'site'; kind: 'landing' | 'impressum' | 'datenschutz' }
  | { page: 'admin'; resetToken?: string }
  | { page: 'brand'; brand: string; eventType: string | null }
  | { page: 'manage'; token: string; mode: 'cancel' | 'reschedule' }
  | { page: 'home' };

function routeFor(pathname: string): Route {
  const clean = pathname.replace(/\/+$/, '') || '/';
  if (clean === '/') return { page: 'site', kind: 'landing' };
  if (clean === '/impressum') return { page: 'site', kind: 'impressum' };
  if (clean === '/datenschutz') return { page: 'site', kind: 'datenschutz' };
  if (pathname === '/admin' || pathname.startsWith('/admin/')) return { page: 'admin' };
  const reset = /^\/passwort-neu\/([A-Za-z0-9_-]+)\/?$/.exec(pathname);
  if (reset) return { page: 'admin', resetToken: reset[1]! };
  const cancel = /^\/absage\/([A-Za-z0-9_-]+)\/?$/.exec(pathname);
  if (cancel) return { page: 'manage', token: cancel[1]!, mode: 'cancel' };
  const reschedule = /^\/umbuchen\/([A-Za-z0-9_-]+)\/?$/.exec(pathname);
  if (reschedule) return { page: 'manage', token: reschedule[1]!, mode: 'reschedule' };
  const match = /^\/([a-z0-9-]+)(?:\/([a-z0-9-]+))?\/?$/i.exec(pathname);
  if (match) return { page: 'brand', brand: match[1]!.toLowerCase(), eventType: match[2]?.toLowerCase() ?? null };
  return { page: 'home' };
}

export function App() {
  const [route] = useState(() => routeFor(window.location.pathname));
  const [meta, setMeta] = useState<Meta | null>(null);
  const site = route.page === 'site';

  useEffect(() => {
    if (site) return; // Das Schaufenster braucht keine Daten vom Server – und muss zum vorgerenderten HTML passen.
    fetchMeta()
      .then(setMeta)
      .catch(() => setMeta({ productName: 'EverCal', registrationOpen: false }));
  }, [site]);

  if (route.page === 'site') return route.kind === 'landing' ? <LandingPage /> : <LegalPage kind={route.kind} />;
  if (!meta) return null;
  if (route.page === 'admin') return <AdminApp meta={meta} resetToken={route.resetToken} />;
  if (route.page === 'brand') return <BrandPage slug={route.brand} eventTypeSlug={route.eventType} />;
  if (route.page === 'manage') return <ManagePage token={route.token} mode={route.mode} />;
  return (
    <main className="page page--message">
      <h1>{meta.productName}</h1>
      <p className="muted">Zum Buchen brauchst du den Link, den du bekommen hast.</p>
      <p>
        <a href="/admin">Zur Verwaltung</a>
      </p>
    </main>
  );
}
